import React from 'react';
import { IoMdRefresh } from 'react-icons/io';
import { MdFileDownload } from 'react-icons/md';
import { Loader2 } from 'lucide-react';
import type { FileFilters } from '../../../../types/file.types';
import { SearchBar } from './SearchBar';
import { FilterButton } from './FilterButton';
import { QuickFilters } from './QuickFilters';
import { BulkActions } from './BulkActions';
import { useAuthContext } from '@/features/auth/providers/AuthProvider';
import type { Table } from '@tanstack/react-table';
import type { File } from '../../../../types/file.types';

interface FileListToolbarProps {
  isLoading: boolean;
  refetch: () => void;
  downloadCsv: () => void;
  isDownloading: boolean;
  searchValue: string;
  setSearchValue: (value: string) => void;
  onlyMyDocs: boolean;
  setOnlyMyDocs: (value: boolean) => void;
  filters: FileFilters[];
  handleToggleFilter: (filter: FileFilters) => void;
  openFilter: boolean;
  setOpenFilter: (value: boolean) => void;
  filterButtonRef: React.RefObject<HTMLDivElement>;
  table: Table<File>;
}

export const FileListToolbar = ({
  isLoading,
  refetch,
  downloadCsv,
  isDownloading,
  searchValue,
  setSearchValue,
  onlyMyDocs,
  setOnlyMyDocs,
  filters,
  handleToggleFilter,
  openFilter,
  setOpenFilter,
  filterButtonRef,
  table,
}: FileListToolbarProps) => {
  const { userInfo } = useAuthContext();

  const [rotationCount, setRotationCount] = React.useState(0);
  const [isHovered, setIsHovered] = React.useState(false);
  const [currentRotation, setCurrentRotation] = React.useState(0);

  React.useEffect(() => {
    let intervalId: number;

    if (isLoading) {
      intervalId = window.setInterval(() => {
        setCurrentRotation((prev) => (prev + 5) % 360);
      }, 10);
    } else {
      setCurrentRotation(0);
    }

    return () => {
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [isLoading]);

  const handleRefreshClick = () => {
    if (!isLoading) {
      setRotationCount((prev) => prev + 1);
      refetch();
    }
  };

  const getRotation = () => {
    if (isLoading) {
      return `rotate(${currentRotation}deg)`;
    }
    const baseRotation = rotationCount * 360;
    const hoverRotation = isHovered ? 360 : 0;
    return `rotate(${baseRotation + hoverRotation}deg)`;
  };

  return (
    <div className="flex flex-col justify-between md:flex-row lg:items-center">
      <div className="flex h-9 items-center">
        <div className="flex items-center space-x-2">
          {/* Bouton Rafraîchir */}
          <div className="border-r border-neutralBG-dark border-opacity-40 pr-1.5 dark:border-neutralBG dark:border-opacity-40">
            <button
              onClick={handleRefreshClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              disabled={isLoading}
              data-tooltip-content="Rafraîchir la liste"
              data-tooltip-id="tooltip"
              data-tooltip-place="bottom"
              data-tooltip-delay-show={100}
              data-tooltip-offset={5}
              className="flex h-9 w-9 items-center justify-center rounded-full transition-all duration-300 ease-in-out hover:scale-105 hover:bg-[#eaeaed] dark:hover:bg-[#545454]"
            >
              <IoMdRefresh
                className={`h-9 text-xl ${isLoading ? '' : 'transition-all duration-500'}`}
                style={{ transform: getRotation() }}
              />
            </button>
          </div>

          {/* Actions groupées */}
          <BulkActions table={table} />

          {/* Boutons de filtrage rapide - cachés si des lignes sont sélectionnées */}
          {table.getSelectedRowModel().rows.length === 0 && (
            <div className="file-list-filters flex items-center space-x-2">
              <QuickFilters
                filters={filters}
                handleToggleFilter={handleToggleFilter}
              />

              {/* Bouton Filtrer */}
              <FilterButton
                openFilter={openFilter}
                setOpenFilter={setOpenFilter}
                filters={filters}
                filterButtonRef={filterButtonRef}
              />
            </div>
          )}
          {userInfo?.admin && (
            <div className="flex flex-col items-center justify-center px-3 border-neutralBG-dark border-opacity-40 dark:border-neutralBG cursor-pointer"
                onClick={() => setOnlyMyDocs(!onlyMyDocs)}>
              <button
                type="button"
                className={`relative inline-flex h-4 w-8 items-center rounded-full transition-colors duration-200 focus:outline-none ${
                  onlyMyDocs ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition duration-200 ease-in-out ${
                    onlyMyDocs ? 'translate-x-4' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-[10px] mt-0.5 font-bold text-gray-400 dark:text-gray-400 select-none leading-none uppercase tracking-tighter">
                Mes docs
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Barre de recherche et export */}
      <div className="mt-2 flex items-center lg:mt-0">
        <div className="file-list-search">
          <SearchBar
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />
        </div>
        <button
          onClick={downloadCsv}
          disabled={isDownloading}
          data-tooltip-content="Télécharger en CSV"
          data-tooltip-id="tooltip"
          data-tooltip-place="bottom"
          data-tooltip-delay-show={100}
          data-tooltip-offset={5}
          className="file-list-download ml-3 flex h-9 items-center rounded-md bg-neutralBG px-3 text-sm shadow-md transition-all duration-300 ease-in-out hover:bg-[#eaeaed] dark:bg-[#333135] dark:hover:bg-[#545454]"
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <MdFileDownload className="text-lg transition-transform duration-300 ease-in-out" />
          )}
        </button>
      </div>
    </div>
  );
};
